import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { salesAPI, hrms as hrmsAPI } from '../../services/api';
import { theme } from '../../theme';
import { useAuth } from '../../contexts/AuthContext';

const ALL_TABS    = ['Overview', 'Checklist', 'Documents', 'Vendors & Quotes', 'Tasks', 'Communications', 'Approvals'];
const LOCKED_TABS = ['Checklist', 'Documents', 'Vendors & Quotes', 'Tasks', 'Communications', 'Approvals'];

const APPROVAL_STAGE_STYLE = {
  draft:       { background: theme.pageBg,  color: theme.textMuted,  border: `1px solid ${theme.border}` },
  pending_cfo: { background: '#FFFBEB',     color: '#92400E',        border: '1px solid #FDE68A' },
  pending_cto: { background: '#EFF6FF',     color: '#1D4ED8',        border: '1px solid #BFDBFE' },
  pending_ceo: { background: '#F5F3FF',     color: '#7C3AED',        border: '1px solid #DDD6FE' },
  approved:    { background: '#ECFDF5',     color: '#065F46',        border: '1px solid #A7F3D0' },
  rejected:    { background: '#FEF2F2',     color: '#991B1B',        border: '1px solid #FECACA' },
};

const APPROVAL_STAGE_LABEL = {
  draft:       'Draft — Not submitted',
  pending_cfo: 'Pending CFO Approval',
  pending_cto: 'Pending CTO Approval',
  pending_ceo: 'Pending CEO Approval',
  approved:    'Approved — All clear',
  rejected:    'Rejected',
};

const STAGE_ORDER = ['draft', 'pending_cfo', 'pending_cto', 'pending_ceo', 'approved'];

const INPUT = {
  border: `1px solid ${theme.border}`, borderRadius: '0.5rem',
  padding: '0.5rem 0.75rem', fontSize: '0.875rem',
  color: theme.text, background: '#fff', outline: 'none', boxSizing: 'border-box',
};

export default function TenderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tender, setTender] = useState(null);
  const [tab, setTab] = useState('Overview');
  const [loading, setLoading] = useState(true);

  const reload = useCallback(() => {
    salesAPI.getTender(id)
      .then(r => setTender(r.data))
      .catch(() => navigate('/sales/tenders'))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  useEffect(() => { reload(); }, [reload]);

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '300px' }}>
      <div style={{ width: '36px', height: '36px', border: `3px solid ${theme.border}`, borderTop: `3px solid ${theme.primary}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );
  if (!tender) return null;

  const role = user?.role || '';
  const isApproved  = tender.approval_stage === 'approved';
  const canSeeAll   = ['cfo', 'cto', 'ceo', 'super_admin', 'sales_head'].includes(role);
  const tabsUnlocked = isApproved || canSeeAll;

  const activeStep = STAGE_ORDER.indexOf(tender.approval_stage);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem' }}>
        <div>
          <button onClick={() => navigate('/sales/tenders')}
            style={{ background: 'none', border: 'none', fontSize: '0.875rem', color: theme.primary, cursor: 'pointer', padding: 0, marginBottom: '0.25rem', display: 'block' }}>
            ← Back to Tenders
          </button>
          <h1 style={{ fontSize: '1.25rem', fontWeight: 700, color: theme.text, margin: 0 }}>
            {tender.account_name || tender.title}
          </h1>
          <p style={{ color: theme.textMuted, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>
            {tender.bd_id || tender.reference_number}
            {tender.account_type ? ` · ${tender.account_type}` : ''}
            {tender.opportunity_type ? ` · ${tender.opportunity_type}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
          <span style={{ ...(APPROVAL_STAGE_STYLE[tender.approval_stage] || APPROVAL_STAGE_STYLE.draft), padding: '0.3rem 0.85rem', borderRadius: '999px', fontSize: '0.8rem', fontWeight: 600 }}>
            {APPROVAL_STAGE_LABEL[tender.approval_stage] || 'Draft'}
          </span>
          {(['ceo','cfo','cto','super_admin','sales_head'].includes(role) || Number(tender.created_by) === Number(user?.id)) && (
            <button
              onClick={async () => {
                if (!window.confirm(`Delete "${tender.account_name || tender.title}"? This cannot be undone.`)) return;
                try {
                  await salesAPI.deleteTender(id);
                  navigate('/sales/tenders');
                } catch (err) {
                  alert(err.response?.data?.detail || 'Failed to delete.');
                }
              }}
              style={{ padding: '0.4rem 0.875rem', fontSize: '0.8rem', fontWeight: 600, border: '1px solid #FECACA', borderRadius: '0.5rem', background: '#fff', color: '#DC2626', cursor: 'pointer' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#FEE2E2'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Approval Progress Bar */}
      <ApprovalProgressBar tender={tender} activeStep={activeStep} />

      {/* Workflow Action Panel (for CFO / CTO / CEO) */}
      {['cfo', 'cto', 'ceo', 'super_admin'].includes(role) && (
        <WorkflowActionPanel tender={tender} userRole={role} onUpdated={reload} />
      )}

      {/* Submit for Approval button (for sales_executive / sales_head when draft/rejected) */}
      {['sales_executive', 'sales_head'].includes(role) && ['draft', 'rejected', null, undefined].includes(tender.approval_stage) && (
        <SubmitForApprovalPanel tenderId={id} approvalStage={tender.approval_stage} onUpdated={reload} />
      )}

      {/* Tab Bar */}
      <div style={{ display: 'flex', gap: '0.25rem', borderBottom: `1px solid ${theme.border}`, overflowX: 'auto' }}>
        {ALL_TABS.map(t => {
          const locked = !tabsUnlocked && LOCKED_TABS.includes(t);
          return (
            <button
              key={t}
              onClick={() => !locked && setTab(t)}
              style={{
                padding: '0.5rem 1rem', fontSize: '0.875rem', fontWeight: 500,
                whiteSpace: 'nowrap', border: 'none', background: 'none',
                cursor: locked ? 'not-allowed' : 'pointer',
                borderBottom: tab === t ? `2px solid ${theme.primary}` : '2px solid transparent',
                color: locked ? theme.border : (tab === t ? theme.primary : theme.textMuted),
                opacity: locked ? 0.5 : 1,
              }}
            >
              {t} {locked ? '🔒' : ''}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      <div>
        {tab === 'Overview'         && <OverviewTab tender={tender} onUpdated={reload} />}
        {tab === 'Checklist'        && tabsUnlocked && <ChecklistTab tenderId={id} />}
        {tab === 'Documents'        && tabsUnlocked && <DocumentsTab tender={tender} onUpdated={reload} />}
        {tab === 'Vendors & Quotes' && tabsUnlocked && <QuotesTab tenderId={id} />}
        {tab === 'Tasks'            && tabsUnlocked && <TasksTab tenderId={id} />}
        {tab === 'Communications'   && tabsUnlocked && <CommunicationsTab tenderId={id} />}
        {tab === 'Approvals'        && tabsUnlocked && <ApprovalsTab tenderId={id} />}
        {LOCKED_TABS.includes(tab) && !tabsUnlocked && (
          <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, padding: '3rem', textAlign: 'center' }}>
            <div style={{ fontSize: '2rem', marginBottom: '0.75rem' }}>🔒</div>
            <p style={{ fontSize: '0.875rem', color: theme.textMuted, margin: 0 }}>
              This section is locked until the opportunity is approved by CEO.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Approval Progress Bar ─────────────────────────────────────────────────────
function ApprovalProgressBar({ tender }) {
  const stage   = tender.approval_stage || 'draft';
  const order   = ['draft', 'pending_cfo', 'pending_cto', 'pending_ceo', 'approved'];
  const current = order.indexOf(stage);
  const rejected = stage === 'rejected';

  const steps = [
    { key: 'draft',       label: 'Submitted' },
    { key: 'pending_cfo', label: 'CFO' },
    { key: 'pending_cto', label: 'CTO' },
    { key: 'pending_ceo', label: 'CEO' },
    { key: 'approved',    label: 'Approved' },
  ];

  return (
    <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, padding: '1rem 1.5rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
        {/* connector line */}
        <div style={{ position: 'absolute', top: '14px', left: '24px', right: '24px', height: '2px', background: theme.border, zIndex: 0 }} />
        {steps.map((s, i) => {
          const done    = !rejected && current > i;
          const active  = !rejected && current === i;
          const isRejected = rejected && i > 0 && i <= current;
          return (
            <div key={s.key} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.35rem', zIndex: 1, flex: 1 }}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.75rem', fontWeight: 700,
                background: isRejected ? '#FEE2E2' : done ? theme.primary : active ? '#fff' : '#fff',
                border: isRejected ? '2px solid #F87171' : done ? `2px solid ${theme.primary}` : active ? `2px solid ${theme.primary}` : `2px solid ${theme.border}`,
                color: isRejected ? '#991B1B' : done ? '#fff' : active ? theme.primary : theme.textMuted,
              }}>
                {isRejected ? '✕' : done ? '✓' : i + 1}
              </div>
              <span style={{ fontSize: '0.7rem', color: active ? theme.primary : theme.textMuted, fontWeight: active ? 600 : 400, whiteSpace: 'nowrap' }}>
                {s.label}
              </span>
            </div>
          );
        })}
      </div>
      {rejected && (
        <p style={{ textAlign: 'center', fontSize: '0.8rem', color: '#991B1B', marginTop: '0.75rem', marginBottom: 0 }}>
          Opportunity was rejected. Sales executive must resubmit after addressing feedback.
        </p>
      )}
    </div>
  );
}

// ── Submit for Approval Panel ─────────────────────────────────────────────────
function SubmitForApprovalPanel({ tenderId, approvalStage, onUpdated }) {
  const [submitting, setSubmitting] = useState(false);
  const submit = async () => {
    if (!window.confirm('Submit this opportunity for CFO approval?')) return;
    setSubmitting(true);
    try {
      await salesAPI.submitForApproval(tenderId);
      onUpdated();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: '0.75rem', padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
      <p style={{ fontSize: '0.875rem', color: '#92400E', margin: 0 }}>
        {approvalStage === 'rejected'
          ? 'This opportunity was rejected. You can resubmit it for CFO approval.'
          : 'This opportunity is in draft. Submit it to start the approval process (CFO → CTO → CEO).'}
      </p>
      <button onClick={submit} disabled={submitting}
        style={{ padding: '0.5rem 1.25rem', fontSize: '0.875rem', background: '#D97706', color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', opacity: submitting ? 0.6 : 1, whiteSpace: 'nowrap' }}>
        {submitting ? 'Submitting…' : 'Submit for Approval'}
      </button>
    </div>
  );
}

// ── Workflow Action Panel (CFO / CTO / CEO) ───────────────────────────────────
function WorkflowActionPanel({ tender, userRole, onUpdated }) {
  const stageMap = { cfo: 'pending_cfo', cto: 'pending_cto', ceo: 'pending_ceo', super_admin: null };
  const myStage  = stageMap[userRole];
  const isMyTurn = myStage ? tender.approval_stage === myStage
                           : ['pending_cfo', 'pending_cto', 'pending_ceo'].includes(tender.approval_stage);

  const [comments, setComments]   = useState('');
  const [assignTo, setAssignTo]   = useState('');
  const [employees, setEmployees] = useState([]);
  const [deciding, setDeciding]   = useState(false);

  const canReassign = ['ceo', 'cto', 'super_admin'].includes(userRole);

  useEffect(() => {
    if (!canReassign || !isMyTurn) return;
    Promise.all([
      hrmsAPI.getEmployees({ role: 'sales_executive' }),
      hrmsAPI.getEmployees({ role: 'sales_head' }),
    ]).then(([execs, heads]) => {
      const all = [...(execs.data.employees || execs.data || []), ...(heads.data.employees || heads.data || [])];
      const unique = all.filter((e, i, arr) => arr.findIndex(x => x.id === e.id) === i);
      setEmployees(unique);
    }).catch(() => {});
  }, [canReassign, isMyTurn]);

  if (!isMyTurn) return null;

  const decide = async (decision) => {
    const label = decision === 'approve' ? 'approve' : 'reject';
    if (!window.confirm(`${label.charAt(0).toUpperCase() + label.slice(1)} this opportunity?`)) return;
    setDeciding(true);
    try {
      await salesAPI.workflowDecision(tender.id, {
        decision,
        comments: comments || null,
        assigned_to: assignTo ? parseInt(assignTo) : null,
      });
      onUpdated();
    } catch (err) {
      alert(err.response?.data?.detail || 'Action failed');
    } finally {
      setDeciding(false);
    }
  };

  const stageLabel = { pending_cfo: 'CFO', pending_cto: 'CTO', pending_ceo: 'CEO' };
  const myLabel = stageLabel[tender.approval_stage] || userRole.toUpperCase();

  return (
    <div style={{ background: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: '0.75rem', padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
      <p style={{ fontSize: '0.875rem', fontWeight: 600, color: '#166534', margin: 0 }}>
        {myLabel} Approval Required
      </p>
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
        <textarea
          value={comments}
          onChange={e => setComments(e.target.value)}
          placeholder="Comments (optional)"
          rows={2}
          style={{ flex: 1, border: `1px solid #86EFAC`, borderRadius: '0.5rem', padding: '0.5rem 0.75rem', fontSize: '0.875rem', resize: 'none', outline: 'none', minWidth: '200px' }}
        />
        {canReassign && employees.length > 0 && (
          <div style={{ minWidth: '200px' }}>
            <label style={{ display: 'block', fontSize: '0.72rem', color: '#166534', fontWeight: 500, marginBottom: '0.25rem' }}>
              Reassign Sales Executive (optional)
            </label>
            <select value={assignTo} onChange={e => setAssignTo(e.target.value)}
              style={{ ...INPUT, borderColor: '#86EFAC', width: '100%' }}>
              <option value="">Keep current</option>
              {employees.map(e => (
                <option key={e.id} value={e.id}>{e.full_name || e.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <button onClick={() => decide('approve')} disabled={deciding}
          style={{ padding: '0.5rem 1.25rem', fontSize: '0.875rem', background: '#16A34A', color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', opacity: deciding ? 0.6 : 1 }}>
          Approve
        </button>
        <button onClick={() => decide('reject')} disabled={deciding}
          style={{ padding: '0.5rem 1.25rem', fontSize: '0.875rem', background: '#DC2626', color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', opacity: deciding ? 0.6 : 1 }}>
          Reject
        </button>
      </div>
    </div>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────────────────
function OverviewTab({ tender, onUpdated }) {
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({
    account_type:           tender.account_type || '',
    account_name:           tender.account_name || '',
    department_name:        tender.department_name || '',
    ministry:               tender.ministry || '',
    state:                  tender.state || '',
    description:            tender.description || '',
    opportunity_type:       tender.opportunity_type || '',
    opp_stage:              tender.opp_stage || 'identified',
    tender_release_date:    tender.tender_release_date ? tender.tender_release_date.split('T')[0] : '',
    tentative_closure_date: tender.tentative_closure_date ? tender.tentative_closure_date.split('T')[0] : '',
    emd_amount:             tender.emd_amount || '',
    selection_method:       tender.selection_method || '',
    estimated_value:        tender.estimated_value || '',
    pq_eligible:            tender.pq_eligible ? 'yes' : 'no',
    tq_eligible:            tender.tq_eligible ? 'yes' : 'no',
    consortium_needed:      tender.consortium_needed ? 'yes' : 'no',
    win_probability:        tender.win_probability ?? 50,
    competitors:            tender.competitors || [],
    client_spoc_name:       tender.client_spoc_name || '',
    client_spoc_contact:    tender.client_spoc_contact || '',
    decision_maker_name:    tender.decision_maker_name || '',
    decision_maker_contact: tender.decision_maker_contact || '',
    client_remarks:         tender.client_remarks || '',
    tender_id_ref:          tender.tender_id_ref || '',
    pq_criteria:            tender.pq_criteria || [],
    tq_criteria:            (tender.tq_criteria || []).map(t =>
      t.scoring_rows
        ? t
        : { ...t, scoring_rows: [{ scoring: '', max_marks: t.max_marks ?? '', actual_marks: t.actual_marks ?? '', remarks: t.remarks || '' }] }
    ),
    min_required_marks:     tender.min_required_marks ?? '',
    tq_available:           (tender.tq_criteria && tender.tq_criteria.length > 0) ? 'yes' : 'no',
  });
  const [competitorInput, setCompetitorInput] = useState('');
  const [saving, setSaving] = useState(false);

  const f = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const addComp = () => {
    const v = competitorInput.trim();
    if (!v) return;
    setForm(p => ({ ...p, competitors: [...p.competitors, v] }));
    setCompetitorInput('');
  };

  const removeComp = (i) => setForm(p => ({ ...p, competitors: p.competitors.filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await salesAPI.updateTender(tender.id, {
        ...form,
        emd_amount:      form.emd_amount      !== '' ? parseFloat(form.emd_amount) : null,
        estimated_value: form.estimated_value !== '' ? parseFloat(form.estimated_value) : null,
        win_probability: parseInt(form.win_probability) || 50,
        pq_eligible: form.pq_criteria && form.pq_criteria.length > 0
          ? form.pq_criteria.every(p => p.status === 'eligible') : false,
        tq_eligible: (form.tq_criteria && form.tq_criteria.length > 0 && form.min_required_marks !== '')
          ? form.tq_criteria.reduce((s, t) => s + (t.scoring_rows || []).reduce((ss, r) => ss + (parseFloat(r.actual_marks) || 0), 0), 0) >= parseFloat(form.min_required_marks) : false,
        consortium_needed: form.consortium_needed === 'yes',
        tender_release_date:    form.tender_release_date    || null,
        tentative_closure_date: form.tentative_closure_date || null,
        min_required_marks: form.min_required_marks !== '' ? parseInt(form.min_required_marks) : null,
        pq_criteria: form.pq_criteria.map((p, i) => ({
          ...p, pq_number: `PQ-${i + 1}`,
          documents: (p.documents || []).filter(d => d.trim()),
        })),
        tq_criteria: form.tq_criteria.map((t, i) => ({
          tq_number: `TQ-${i + 1}`,
          description: t.description || '',
          scoring_rows: (t.scoring_rows || []).map(r => ({
            scoring:      r.scoring      || '',
            max_marks:    parseFloat(r.max_marks)    || 0,
            actual_marks: parseFloat(r.actual_marks) || 0,
            remarks:      r.remarks      || '',
          })),
        })),
      });
      setEdit(false);
      onUpdated();
    } finally {
      setSaving(false);
    }
  };

  const fmt = (n) => n ? `₹${Number(n).toLocaleString()}` : '—';
  const yn  = (v) => v ? 'Yes' : 'No';

  if (!edit) return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      {/* Opportunity Details Card */}
      <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, padding: '1.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: theme.text, margin: 0 }}>Opportunity Details</h3>
          <button onClick={() => setEdit(true)} style={{ background: 'none', border: 'none', fontSize: '0.875rem', color: theme.primary, cursor: 'pointer' }}>Edit</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1.25rem', fontSize: '0.875rem' }}>
          <Info label="BD ID"               value={tender.bd_id || '—'} />
          <Info label="Tender ID"           value={tender.tender_id_ref || '—'} />
          <Info label="Created By"          value={tender.creator_name || '—'} />
          <Info label="Assigned To"         value={tender.assignee_name || '—'} />
          <Info label="Account Type"        value={tender.account_type || '—'} />
          <Info label="Account Name"        value={tender.account_name || '—'} />
          <Info label="Department"          value={tender.department_name || '—'} />
          <Info label="Ministry"            value={tender.ministry || '—'} />
          <Info label="State"               value={tender.state || '—'} />
          <Info label="Opportunity Type"    value={tender.opportunity_type || '—'} />
          <Info label="Stage"               value={(tender.opp_stage || 'identified').replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())} />
          <Info label="Opportunity Date"    value={tender.opportunity_date ? new Date(tender.opportunity_date).toLocaleDateString('en-IN') : '—'} />
          <Info label="Tender Release Date" value={tender.tender_release_date ? new Date(tender.tender_release_date).toLocaleDateString('en-IN') : '—'} />
          <Info label="Closure Date"        value={tender.tentative_closure_date ? new Date(tender.tentative_closure_date).toLocaleDateString('en-IN') : '—'} />
          <Info label="EMD"                 value={fmt(tender.emd_amount)} />
          <Info label="Selection Method"    value={tender.selection_method || '—'} />
          <Info label="Estimated Value"     value={fmt(tender.estimated_value)} />
          <Info label="Win Probability"     value={`${tender.win_probability || 0}%`} />
          <Info label="PQ Eligible" value={(() => {
            if (!tender.pq_criteria || tender.pq_criteria.length === 0) return 'N/A';
            return tender.pq_criteria.every(p => p.status === 'eligible') ? '✓ Eligible' : '✗ Not Eligible';
          })()} />
          <Info label="TQ Eligible" value={(() => {
            if (!tender.tq_criteria || tender.tq_criteria.length === 0) return 'N/A';
            const total = tender.tq_criteria.reduce((s, t) => s + (t.actual_marks || 0), 0);
            if (tender.min_required_marks == null) return 'N/A';
            return total >= tender.min_required_marks ? '✓ Eligible' : '✗ Not Eligible';
          })()} />
          <Info label="Consortium Needed"   value={yn(tender.consortium_needed)} />
        </div>
        {tender.description && (
          <div style={{ marginTop: '1.25rem' }}>
            <p style={{ fontSize: '0.72rem', color: theme.textMuted, fontWeight: 500, margin: 0 }}>Description</p>
            <p style={{ fontSize: '0.875rem', color: theme.text, margin: '0.2rem 0 0', whiteSpace: 'pre-line' }}>{tender.description}</p>
          </div>
        )}
        {tender.competitors && tender.competitors.length > 0 && (
          <div style={{ marginTop: '1.25rem' }}>
            <p style={{ fontSize: '0.72rem', color: theme.textMuted, fontWeight: 500, margin: '0 0 0.4rem' }}>Competitors</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
              {tender.competitors.map((c, i) => (
                <span key={i} style={{ background: '#EEF2FF', color: '#4338CA', padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.8rem' }}>{c}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Client Info Card */}
      <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, padding: '1.25rem' }}>
        <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: theme.text, margin: '0 0 1rem' }}>Client Info</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1.25rem', fontSize: '0.875rem' }}>
          <Info label="Client SPOC"          value={tender.client_spoc_name || '—'} />
          <Info label="SPOC Contact"         value={tender.client_spoc_contact || '—'} />
          <Info label="Decision Maker"       value={tender.decision_maker_name || '—'} />
          <Info label="Decision Maker Contact" value={tender.decision_maker_contact || '—'} />
        </div>
        {tender.client_remarks && (
          <div style={{ marginTop: '1.25rem' }}>
            <p style={{ fontSize: '0.72rem', color: theme.textMuted, fontWeight: 500, margin: 0 }}>Remarks</p>
            <p style={{ fontSize: '0.875rem', color: theme.text, margin: '0.2rem 0 0' }}>{tender.client_remarks}</p>
          </div>
        )}
      </div>

      {/* PQ Criteria Card */}
      {tender.pq_criteria && tender.pq_criteria.length > 0 && (() => {
        const allEligible = tender.pq_criteria.every(p => p.status === 'eligible');
        return (
          <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, padding: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: theme.text, margin: 0 }}>Pre-Qualification Criteria (PQ)</h3>
              <span style={{ fontSize: '0.78rem', fontWeight: 700, padding: '0.2rem 0.75rem', borderRadius: '999px', background: allEligible ? '#D1FAE5' : '#FEE2E2', color: allEligible ? '#065F46' : '#991B1B' }}>
                Overall PQ: {allEligible ? 'Eligible ✓' : 'Not Eligible ✗'}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {tender.pq_criteria.map((pq, i) => (
                <div key={i} style={{ border: `1px solid ${theme.border}`, borderRadius: '0.5rem', padding: '0.875rem', background: pq.status === 'eligible' ? '#F0FDF4' : '#FFF1F2' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: '0.75rem', fontWeight: 700, color: theme.primary }}>{pq.pq_number || `PQ-${i + 1}`}</span>
                      <p style={{ margin: '0.25rem 0 0', fontSize: '0.875rem', color: theme.text }}>{pq.condition}</p>
                      {pq.documents && pq.documents.filter(d => d).length > 0 && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <p style={{ fontSize: '0.72rem', color: theme.textMuted, fontWeight: 600, margin: '0 0 0.25rem' }}>Document Proofs:</p>
                          <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                            {pq.documents.filter(d => d).map((d, di) => (
                              <li key={di} style={{ fontSize: '0.8rem', color: theme.text }}>{d}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    <span style={{ flexShrink: 0, fontSize: '0.75rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: '999px', background: pq.status === 'eligible' ? '#D1FAE5' : '#FEE2E2', color: pq.status === 'eligible' ? '#065F46' : '#991B1B' }}>
                      {pq.status === 'eligible' ? 'Eligible' : 'Not Eligible'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* TQ Criteria Card */}
      {tender.tq_criteria && tender.tq_criteria.length > 0 && (() => {
        const tqList = tender.tq_criteria.map(t =>
          t.scoring_rows ? t : { ...t, scoring_rows: [{ scoring: '', max_marks: t.max_marks ?? 0, actual_marks: t.actual_marks ?? 0, remarks: t.remarks || '' }] }
        );
        const totalMax    = tqList.reduce((s, t) => s + (t.scoring_rows || []).reduce((ss, r) => ss + (parseFloat(r.max_marks) || 0), 0), 0);
        const totalActual = tqList.reduce((s, t) => s + (t.scoring_rows || []).reduce((ss, r) => ss + (parseFloat(r.actual_marks) || 0), 0), 0);
        const minReq      = tender.min_required_marks;
        const tqEligible  = minReq != null ? totalActual >= minReq : null;
        const TH = ({ children, align = 'left', width }) => (
          <th style={{ padding: '0.4rem 0.75rem', textAlign: align, width, fontSize: '0.72rem', fontWeight: 600, color: theme.textMuted, borderBottom: `1px solid ${theme.border}`, whiteSpace: 'nowrap' }}>{children}</th>
        );
        return (
          <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, padding: '1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
              <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: theme.text, margin: 0 }}>Technical Qualification Criteria (TQ)</h3>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <span style={{ fontSize: '0.78rem', color: theme.textMuted }}>
                  Total: <strong>{totalActual}</strong> / {totalMax}
                  {minReq != null && <> · Min Required: <strong>{minReq}</strong></>}
                </span>
                {tqEligible !== null && (
                  <span style={{ fontSize: '0.78rem', fontWeight: 700, padding: '0.2rem 0.75rem', borderRadius: '999px', background: tqEligible ? '#D1FAE5' : '#FEE2E2', color: tqEligible ? '#065F46' : '#991B1B' }}>
                    TQ: {tqEligible ? 'Eligible ✓' : 'Not Eligible ✗'}
                  </span>
                )}
              </div>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ background: theme.pageBg }}>
                    <TH width="80px">TQ No.</TH>
                    <TH width="200px">Criteria Description</TH>
                    <TH width="auto">Scoring</TH>
                    <TH align="right" width="100px">Max Marks</TH>
                    <TH align="right" width="110px">Actual Marks</TH>
                    <TH width="160px">Remarks</TH>
                  </tr>
                </thead>
                <tbody>
                  {tqList.map((tq, i) => {
                    const rows = tq.scoring_rows || [];
                    const tqMax    = rows.reduce((s, r) => s + (parseFloat(r.max_marks) || 0), 0);
                    const tqActual = rows.reduce((s, r) => s + (parseFloat(r.actual_marks) || 0), 0);
                    return rows.map((r, j) => (
                      <tr key={`${i}-${j}`} style={{ borderBottom: `1px solid ${theme.border}` }}>
                        {j === 0 && (
                          <>
                            <td rowSpan={rows.length} style={{ padding: '0.5rem 0.75rem', fontWeight: 700, color: theme.primary, whiteSpace: 'nowrap', verticalAlign: 'top', borderRight: `1px solid ${theme.border}` }}>{tq.tq_number || `TQ-${i + 1}`}</td>
                            <td rowSpan={rows.length} style={{ padding: '0.5rem 0.75rem', color: theme.text, verticalAlign: 'top', borderRight: `1px solid ${theme.border}` }}>
                              <div>{tq.description || '—'}</div>
                              {rows.length > 1 && <div style={{ marginTop: '0.4rem', fontSize: '0.7rem', color: theme.textMuted }}>Sub-total: {tqActual} / {tqMax}</div>}
                            </td>
                          </>
                        )}
                        <td style={{ padding: '0.5rem 0.75rem', color: theme.text }}>{r.scoring || '—'}</td>
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: theme.text }}>{r.max_marks ?? '—'}</td>
                        <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 600, color: theme.text }}>{r.actual_marks ?? '—'}</td>
                        <td style={{ padding: '0.5rem 0.75rem', color: theme.textMuted }}>{r.remarks || '—'}</td>
                      </tr>
                    ));
                  })}
                  <tr style={{ background: theme.pageBg, fontWeight: 700 }}>
                    <td colSpan={3} style={{ padding: '0.5rem 0.75rem', fontSize: '0.8rem', color: theme.textMuted }}>Total</td>
                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: theme.text }}>{totalMax}</td>
                    <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', color: theme.text }}>{totalActual}</td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
    </div>
  );

  // ── Edit mode ──
  const LABEL = { display: 'block', fontSize: '0.75rem', fontWeight: 500, color: theme.textMuted, marginBottom: '0.25rem' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: theme.text, margin: 0 }}>Opportunity Details</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          {[
            { key: 'tender_id_ref', label: 'Tender ID', type: 'text' },
            { key: 'account_name',  label: 'Account Name', type: 'text' },
            { key: 'department_name', label: 'Department', type: 'text' },
            { key: 'ministry',      label: 'Ministry', type: 'text' },
            { key: 'state',         label: 'State', type: 'text' },
          ].map(({ key, label, type }) => (
            <div key={key}>
              <label style={LABEL}>{label}</label>
              <input type={type} value={form[key]} onChange={e => f(key, e.target.value)} style={{ ...INPUT, width: '100%' }} />
            </div>
          ))}
          <div>
            <label style={LABEL}>Account Type</label>
            <select value={form.account_type} onChange={e => f('account_type', e.target.value)} style={{ ...INPUT, width: '100%' }}>
              <option value="">Select…</option>
              {['Govt', 'Enterprises', 'Banking'].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label style={LABEL}>Opportunity Type</label>
            <select value={form.opportunity_type} onChange={e => f('opportunity_type', e.target.value)} style={{ ...INPUT, width: '100%' }}>
              <option value="">Select…</option>
              {['Reactive RFP','Reactive RFQ','Reactive EOI','Proactive RFP','Proactive RFQ','Proactive EOI'].map(v => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={LABEL}>Stage</label>
            <select value={form.opp_stage} onChange={e => f('opp_stage', e.target.value)} style={{ ...INPUT, width: '100%' }}>
              {['identified','interaction','proposal','technically_qualified','won','lost','dropped','cancelled'].map(s => (
                <option key={s} value={s}>{s.replace('_',' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={LABEL}>Selection Method</label>
            <select value={form.selection_method} onChange={e => f('selection_method', e.target.value)} style={{ ...INPUT, width: '100%' }}>
              <option value="">Select…</option>
              {['L1','QCBS','Nomination'].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          {[
            { key: 'tender_release_date',    label: 'Tender Release Date', type: 'date' },
            { key: 'tentative_closure_date', label: 'Closure Date', type: 'date' },
            { key: 'emd_amount',             label: 'EMD (₹)', type: 'number' },
            { key: 'estimated_value',        label: 'Estimated Value (₹)', type: 'number' },
            { key: 'win_probability',        label: 'Win Probability (%)', type: 'number' },
          ].map(({ key, label, type }) => (
            <div key={key}>
              <label style={LABEL}>{label}</label>
              <input type={type} value={form[key]} onChange={e => f(key, e.target.value)} style={{ ...INPUT, width: '100%' }} />
            </div>
          ))}
          {/* PQ Eligible — auto-derived, read-only */}
          <div>
            <label style={LABEL}>PQ Eligible (Auto)</label>
            {(() => {
              const pqStatus = form.pq_criteria && form.pq_criteria.length > 0
                ? (form.pq_criteria.every(p => p.status === 'eligible') ? 'eligible' : 'not_eligible')
                : null;
              return (
                <div style={{ ...INPUT, width: '100%', background: theme.pageBg, display: 'flex', alignItems: 'center', height: '36px', boxSizing: 'border-box' }}>
                  {pqStatus ? (
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, padding: '0.15rem 0.6rem', borderRadius: '999px',
                      background: pqStatus === 'eligible' ? '#D1FAE5' : '#FEE2E2',
                      color: pqStatus === 'eligible' ? '#065F46' : '#991B1B' }}>
                      {pqStatus === 'eligible' ? 'Eligible' : 'Not Eligible'}
                    </span>
                  ) : <span style={{ fontSize: '0.78rem', color: theme.textMuted }}>Derived from PQ criteria</span>}
                </div>
              );
            })()}
          </div>
          {/* TQ Eligible — auto-derived, read-only */}
          <div>
            <label style={LABEL}>TQ Eligible (Auto)</label>
            {(() => {
              const tqStatus = (form.tq_criteria && form.tq_criteria.length > 0 && form.min_required_marks !== '')
                ? (form.tq_criteria.reduce((s,t)=>s+(t.scoring_rows||[]).reduce((ss,r)=>ss+(parseFloat(r.actual_marks)||0),0),0) >= parseFloat(form.min_required_marks) ? 'eligible' : 'not_eligible')
                : null;
              return (
                <div style={{ ...INPUT, width: '100%', background: theme.pageBg, display: 'flex', alignItems: 'center', height: '36px', boxSizing: 'border-box' }}>
                  {tqStatus ? (
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, padding: '0.15rem 0.6rem', borderRadius: '999px',
                      background: tqStatus === 'eligible' ? '#D1FAE5' : '#FEE2E2',
                      color: tqStatus === 'eligible' ? '#065F46' : '#991B1B' }}>
                      {tqStatus === 'eligible' ? 'Eligible' : 'Not Eligible'}
                    </span>
                  ) : <span style={{ fontSize: '0.78rem', color: theme.textMuted }}>Derived from TQ criteria</span>}
                </div>
              );
            })()}
          </div>
          {/* Consortium Needed */}
          <div>
            <label style={LABEL}>Consortium Needed</label>
            <select value={form.consortium_needed} onChange={e => f('consortium_needed', e.target.value)} style={{ ...INPUT, width: '100%' }}>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={LABEL}>Description</label>
            <textarea rows={3} value={form.description} onChange={e => f('description', e.target.value)}
              style={{ ...INPUT, width: '100%', resize: 'vertical' }} />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={LABEL}>Competitors</label>
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <input value={competitorInput} onChange={e => setCompetitorInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addComp(); } }}
                placeholder="Add competitor" style={{ ...INPUT, flex: 1, width: 'auto' }} />
              <button type="button" onClick={addComp}
                style={{ padding: '0.5rem 0.875rem', fontSize: '0.875rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer' }}>Add</button>
            </div>
            {form.competitors.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                {form.competitors.map((c, i) => (
                  <span key={i} style={{ background: '#EEF2FF', color: '#4338CA', padding: '0.2rem 0.6rem', borderRadius: '999px', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                    {c}
                    <button type="button" onClick={() => removeComp(i)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4338CA', fontWeight: 700, lineHeight: 1, padding: 0 }}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, padding: '1.25rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: theme.text, margin: 0 }}>Client Info</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          {[
            { key: 'client_spoc_name',       label: 'Client SPOC Name' },
            { key: 'client_spoc_contact',    label: 'Client SPOC Contact' },
            { key: 'decision_maker_name',    label: 'Decision Maker Name' },
            { key: 'decision_maker_contact', label: 'Decision Maker Contact' },
          ].map(({ key, label }) => (
            <div key={key}>
              <label style={LABEL}>{label}</label>
              <input value={form[key]} onChange={e => f(key, e.target.value)} style={{ ...INPUT, width: '100%' }} />
            </div>
          ))}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={LABEL}>Remarks</label>
            <textarea rows={2} value={form.client_remarks} onChange={e => f('client_remarks', e.target.value)}
              style={{ ...INPUT, width: '100%', resize: 'vertical' }} />
          </div>
        </div>
      </div>

      {/* PQ Criteria edit */}
      <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, padding: '1.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: theme.text, margin: 0 }}>Pre-Qualification Criteria (PQ)</h3>
          {form.pq_criteria.length > 0 && (
            <span style={{ fontSize: '0.75rem', fontWeight: 700, padding: '0.2rem 0.6rem', borderRadius: '999px',
              background: form.pq_criteria.every(p => p.status === 'eligible') ? '#D1FAE5' : '#FEE2E2',
              color: form.pq_criteria.every(p => p.status === 'eligible') ? '#065F46' : '#991B1B' }}>
              Overall: {form.pq_criteria.every(p => p.status === 'eligible') ? 'Eligible ✓' : 'Not Eligible ✗'}
            </span>
          )}
        </div>
        {form.pq_criteria.map((pq, i) => (
          <div key={i} style={{ border: `1px solid ${theme.border}`, borderRadius: '0.5rem', padding: '0.75rem', marginBottom: '0.6rem', background: theme.pageBg }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.75rem', fontWeight: 700, color: theme.primary }}>PQ-{i + 1}</span>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <select value={pq.status} onChange={e => { const r=[...form.pq_criteria]; r[i]={...r[i],status:e.target.value}; f('pq_criteria',r); }}
                  style={{ ...INPUT, width:'auto', fontSize:'0.75rem', padding:'0.2rem 0.4rem',
                    background: pq.status==='eligible' ? '#D1FAE5':'#FEE2E2',
                    color: pq.status==='eligible' ? '#065F46':'#991B1B', fontWeight:700, border:'none' }}>
                  <option value="eligible">Eligible</option>
                  <option value="not_eligible">Not Eligible</option>
                </select>
                <button type="button" onClick={() => f('pq_criteria', form.pq_criteria.filter((_,idx)=>idx!==i))}
                  style={{ background:'none',border:'none',color:'#EF4444',cursor:'pointer',fontSize:'1rem' }}>×</button>
              </div>
            </div>
            <label style={LABEL}>Eligibility Condition</label>
            <input value={pq.condition||''} onChange={e => { const r=[...form.pq_criteria]; r[i]={...r[i],condition:e.target.value}; f('pq_criteria',r); }}
              style={{ ...INPUT, width:'100%', marginBottom:'0.5rem' }} />
            <label style={LABEL}>Document Proofs</label>
            {(pq.documents||['']).map((doc, di) => (
              <div key={di} style={{ display:'flex', gap:'0.4rem', marginBottom:'0.3rem' }}>
                <input value={doc} onChange={e => { const r=[...form.pq_criteria]; const docs=[...r[i].documents]; docs[di]=e.target.value; r[i]={...r[i],documents:docs}; f('pq_criteria',r); }}
                  style={{ ...INPUT, flex:1 }} placeholder={`Proof ${di+1}`} />
                {(pq.documents||[]).length>1 && (
                  <button type="button" onClick={() => { const r=[...form.pq_criteria]; r[i]={...r[i],documents:r[i].documents.filter((_,didx)=>didx!==di)}; f('pq_criteria',r); }}
                    style={{ background:'none',border:`1px solid ${theme.border}`,borderRadius:'0.375rem',color:theme.textMuted,cursor:'pointer',padding:'0.25rem 0.5rem' }}>×</button>
                )}
              </div>
            ))}
            <button type="button" onClick={() => { const r=[...form.pq_criteria]; r[i]={...r[i],documents:[...(r[i].documents||[]),''] }; f('pq_criteria',r); }}
              style={{ background:'none',border:'none',color:theme.primary,cursor:'pointer',fontSize:'0.78rem',fontWeight:600,padding:0 }}>+ Add document</button>
          </div>
        ))}
        <button type="button" onClick={() => f('pq_criteria',[...form.pq_criteria,{condition:'',documents:[''],status:'eligible'}])}
          style={{ padding:'0.4rem 1rem',background:'#EEF2FF',color:'#4338CA',border:'1px dashed #A5B4FC',borderRadius:'0.5rem',cursor:'pointer',fontSize:'0.82rem',fontWeight:600 }}>
          + Add PQ Criteria
        </button>
      </div>

      {/* TQ Criteria edit */}
      <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, padding: '1.25rem' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap:'wrap', gap:'0.5rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 700, color: theme.text, margin: 0 }}>Technical Qualification Criteria (TQ)</h3>
          {form.tq_available === 'yes' && form.tq_criteria.length > 0 && form.min_required_marks !== '' && (() => {
            const total = form.tq_criteria.reduce((s,t)=>s+(t.scoring_rows||[]).reduce((ss,r)=>ss+(parseFloat(r.actual_marks)||0),0),0);
            const eligible = total >= parseFloat(form.min_required_marks);
            return (
              <span style={{ fontSize:'0.75rem', fontWeight:700, padding:'0.2rem 0.6rem', borderRadius:'999px', background: eligible ? '#D1FAE5':'#FEE2E2', color: eligible ? '#065F46':'#991B1B' }}>
                TQ: {eligible ? 'Eligible ✓' : 'Not Eligible ✗'}
              </span>
            );
          })()}
        </div>

        {/* TQ Available toggle */}
        <div style={{ display:'flex', alignItems:'center', gap:'0.75rem', marginBottom:'1rem', padding:'0.75rem', background:theme.pageBg, borderRadius:'0.5rem', border:`1px solid ${theme.border}` }}>
          <label style={{ fontSize:'0.85rem', fontWeight:600, color:theme.text, margin:0 }}>TQ Criteria Available?</label>
          <div style={{ display:'flex', gap:'0.5rem' }}>
            {['yes','no'].map(v => (
              <button key={v} type="button"
                onClick={() => { f('tq_available', v); if (v === 'no') { f('tq_criteria', []); f('min_required_marks', ''); } }}
                style={{ padding:'0.35rem 1rem', fontSize:'0.82rem', fontWeight:600, borderRadius:'0.4rem', cursor:'pointer', border:'none',
                  background: form.tq_available === v ? (v === 'yes' ? theme.primary : '#EF4444') : theme.border,
                  color: form.tq_available === v ? '#fff' : theme.textMuted }}>
                {v === 'yes' ? 'Yes' : 'No'}
              </button>
            ))}
          </div>
        </div>

        {form.tq_available === 'yes' && (
          <>
            {form.tq_criteria.map((tq, i) => {
              const rows = tq.scoring_rows || [];
              const tqMax    = rows.reduce((s, r) => s + (parseFloat(r.max_marks) || 0), 0);
              const tqActual = rows.reduce((s, r) => s + (parseFloat(r.actual_marks) || 0), 0);
              const updateTQ = (patch) => { const c = [...form.tq_criteria]; c[i] = { ...c[i], ...patch }; f('tq_criteria', c); };
              const updateRow = (j, patch) => { const c = [...form.tq_criteria]; const rr = [...(c[i].scoring_rows||[])]; rr[j] = { ...rr[j], ...patch }; c[i] = { ...c[i], scoring_rows: rr }; f('tq_criteria', c); };
              const removeRow = (j) => { const c = [...form.tq_criteria]; c[i] = { ...c[i], scoring_rows: rows.filter((_,idx)=>idx!==j) }; f('tq_criteria', c); };
              const addRow    = () => { updateTQ({ scoring_rows: [...rows, { scoring:'', max_marks:'', actual_marks:'', remarks:'' }] }); };
              return (
                <div key={i} style={{ border:`1.5px solid ${theme.border}`, borderRadius:'0.625rem', marginBottom:'0.875rem', overflow:'hidden' }}>
                  {/* TQ header */}
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0.6rem 0.875rem', background:theme.pageBg, borderBottom:`1px solid ${theme.border}` }}>
                    <span style={{ fontSize:'0.8rem', fontWeight:700, color:theme.primary }}>TQ-{i+1}</span>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.75rem' }}>
                      {rows.length > 0 && <span style={{ fontSize:'0.72rem', color:theme.textMuted }}>Sub-total: {tqActual} / {tqMax}</span>}
                      <button type="button" onClick={() => f('tq_criteria', form.tq_criteria.filter((_,idx)=>idx!==i))}
                        style={{ background:'none',border:'none',color:'#EF4444',cursor:'pointer',fontSize:'1rem',lineHeight:1 }}>×</button>
                    </div>
                  </div>
                  {/* Criteria description */}
                  <div style={{ padding:'0.75rem 0.875rem', borderBottom:`1px solid ${theme.border}` }}>
                    <label style={LABEL}>Criteria Description</label>
                    <input value={tq.description||''} onChange={e => updateTQ({ description: e.target.value })} style={{ ...INPUT, width:'100%' }} placeholder="e.g. Financial Capability" />
                  </div>
                  {/* Scoring rows table */}
                  <div style={{ overflowX:'auto' }}>
                    <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
                      <thead>
                        <tr style={{ background:'#f9fafb' }}>
                          {['Scoring / Parameter', 'Max Marks', 'Actual Marks', 'Remarks (Optional)', ''].map((h, hi) => (
                            <th key={hi} style={{ padding:'0.4rem 0.625rem', textAlign: hi >= 1 && hi <= 2 ? 'right' : 'left', fontSize:'0.7rem', fontWeight:600, color:theme.textMuted, borderBottom:`1px solid ${theme.border}`, whiteSpace:'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((r, j) => (
                          <tr key={j} style={{ borderBottom:`1px solid ${theme.border}` }}>
                            <td style={{ padding:'0.4rem 0.625rem' }}>
                              <input value={r.scoring||''} onChange={e => updateRow(j,{ scoring:e.target.value })} style={{ ...INPUT, width:'100%', minWidth:160 }} placeholder="e.g. Annual Turnover" />
                            </td>
                            <td style={{ padding:'0.4rem 0.625rem' }}>
                              <input type="number" min="0" value={r.max_marks||''} onChange={e => updateRow(j,{ max_marks:e.target.value })} style={{ ...INPUT, width:90, textAlign:'right' }} placeholder="0" />
                            </td>
                            <td style={{ padding:'0.4rem 0.625rem' }}>
                              <input type="number" min="0" value={r.actual_marks||''} onChange={e => updateRow(j,{ actual_marks:e.target.value })} style={{ ...INPUT, width:90, textAlign:'right' }} placeholder="0" />
                            </td>
                            <td style={{ padding:'0.4rem 0.625rem' }}>
                              <input value={r.remarks||''} onChange={e => updateRow(j,{ remarks:e.target.value })} style={{ ...INPUT, width:'100%', minWidth:120 }} placeholder="Optional" />
                            </td>
                            <td style={{ padding:'0.4rem 0.625rem', textAlign:'center' }}>
                              {rows.length > 1 && (
                                <button type="button" onClick={() => removeRow(j)} style={{ background:'none',border:'none',color:'#EF4444',cursor:'pointer',fontSize:'0.85rem',padding:'0 0.25rem' }}>×</button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Add scoring row */}
                  <div style={{ padding:'0.5rem 0.875rem' }}>
                    <button type="button" onClick={addRow}
                      style={{ padding:'0.3rem 0.875rem', background:'#EEF2FF', color:'#4338CA', border:'1px dashed #A5B4FC', borderRadius:'0.4rem', cursor:'pointer', fontSize:'0.78rem', fontWeight:600 }}>
                      + Add Scoring Row
                    </button>
                  </div>
                </div>
              );
            })}
            <div style={{ display:'flex', gap:'1rem', alignItems:'center', flexWrap:'wrap', marginTop:'0.5rem' }}>
              <button type="button"
                onClick={() => f('tq_criteria',[...form.tq_criteria,{ description:'', scoring_rows:[{ scoring:'', max_marks:'', actual_marks:'', remarks:'' }] }])}
                style={{ padding:'0.4rem 1rem',background:'#EEF2FF',color:'#4338CA',border:'1px dashed #A5B4FC',borderRadius:'0.5rem',cursor:'pointer',fontSize:'0.82rem',fontWeight:600 }}>
                + Add TQ Criteria
              </button>
              {form.tq_criteria.length > 0 && (
                <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
                  <label style={{ ...LABEL, margin:0, whiteSpace:'nowrap' }}>Min Required Marks:</label>
                  <input type="number" min="0" value={form.min_required_marks} onChange={e => f('min_required_marks', e.target.value)}
                    style={{ ...INPUT, width:'90px' }} placeholder="0" />
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem' }}>
        <button onClick={() => setEdit(false)} style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', border: `1px solid ${theme.border}`, borderRadius: '0.5rem', background: '#fff', cursor: 'pointer', color: theme.textMuted }}>Cancel</button>
        <button onClick={save} disabled={saving} style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p style={{ fontSize: '0.72rem', color: theme.textMuted, fontWeight: 500, margin: 0 }}>{label}</p>
      <p style={{ fontSize: '0.875rem', color: theme.text, margin: '0.2rem 0 0' }}>{value}</p>
    </div>
  );
}

// ── Checklist Tab ─────────────────────────────────────────────────────────────
function ChecklistTab({ tenderId }) {
  const [items, setItems] = useState([]);
  const [adding, setAdding] = useState(false);
  const [newItem, setNewItem] = useState({ title: '', category: 'general', due_date: '' });

  const load = useCallback(() => {
    salesAPI.getChecklist(tenderId).then(r => setItems(r.data)).catch(console.error);
  }, [tenderId]);

  useEffect(() => { load(); }, [load]);

  const toggle = async (item) => {
    await salesAPI.updateCheckItem(tenderId, item.id, { status: item.status === 'done' ? 'pending' : 'done' });
    load();
  };

  const add = async (e) => {
    e.preventDefault();
    await salesAPI.addCheckItem(tenderId, newItem);
    setNewItem({ title: '', category: 'general', due_date: '' });
    setAdding(false);
    load();
  };

  const del = async (cid) => {
    if (!window.confirm('Delete this item?')) return;
    await salesAPI.deleteCheckItem(tenderId, cid);
    load();
  };

  const done = items.filter(i => i.status === 'done').length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <p style={{ fontSize: '0.875rem', color: theme.textMuted, margin: 0 }}>{done}/{items.length} completed</p>
        <button onClick={() => setAdding(true)} style={{ background: 'none', border: 'none', fontSize: '0.875rem', color: theme.primary, cursor: 'pointer' }}>+ Add Item</button>
      </div>
      {adding && (
        <form onSubmit={add} style={{ background: theme.lightBg, border: `1px solid ${theme.primaryLight}`, borderRadius: '0.75rem', padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <div style={{ gridColumn: '1 / -1' }}>
              <input required placeholder="Item title" value={newItem.title}
                onChange={e => setNewItem(p => ({ ...p, title: e.target.value }))}
                style={{ ...INPUT, width: '100%' }} />
            </div>
            <select value={newItem.category} onChange={e => setNewItem(p => ({ ...p, category: e.target.value }))} style={INPUT}>
              {['general','eligibility','technical','financial','legal','submission'].map(c =>
                <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
              )}
            </select>
            <input type="date" value={newItem.due_date} onChange={e => setNewItem(p => ({ ...p, due_date: e.target.value }))} style={INPUT} />
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
            <button type="button" onClick={() => setAdding(false)} style={{ padding: '0.35rem 0.75rem', fontSize: '0.875rem', border: `1px solid ${theme.border}`, borderRadius: '0.5rem', background: '#fff', cursor: 'pointer', color: theme.textMuted }}>Cancel</button>
            <button type="submit" style={{ padding: '0.35rem 0.75rem', fontSize: '0.875rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer' }}>Add</button>
          </div>
        </form>
      )}
      <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}` }}>
        {items.length === 0 ? (
          <p style={{ padding: '1.5rem', textAlign: 'center', color: theme.textMuted, fontSize: '0.875rem' }}>No checklist items yet</p>
        ) : items.map((item, i) => (
          <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', borderBottom: i < items.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
            <input type="checkbox" checked={item.status === 'done'} onChange={() => toggle(item)}
              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: theme.primary }} />
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: '0.875rem', color: item.status === 'done' ? theme.textMuted : theme.text, textDecoration: item.status === 'done' ? 'line-through' : 'none' }}>{item.title}</span>
              <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: theme.textMuted, textTransform: 'capitalize' }}>{item.category}</span>
              {item.due_date && <span style={{ marginLeft: '0.5rem', fontSize: '0.75rem', color: theme.warning }}>Due {new Date(item.due_date).toLocaleDateString('en-IN')}</span>}
            </div>
            <button onClick={() => del(item.id)} style={{ background: 'none', border: 'none', color: theme.border, cursor: 'pointer', fontSize: '0.875rem' }}
              onMouseEnter={e => e.currentTarget.style.color = theme.danger}
              onMouseLeave={e => e.currentTarget.style.color = theme.border}>✕</button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Documents Tab ─────────────────────────────────────────────────────────────
function DocumentsTab({ tender, onUpdated }) {
  const [form, setForm] = useState({ document_name: '', document_url: '', version: '1', notes: '' });
  const [saving, setSaving] = useState(false);

  const upload = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await salesAPI.uploadDocument(tender.id, form);
      setForm({ document_name: '', document_url: '', version: '1', notes: '' });
      onUpdated();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <form onSubmit={upload} style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: theme.text, margin: 0 }}>Add Document</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <input required placeholder="Document name" value={form.document_name} onChange={e => setForm(p => ({ ...p, document_name: e.target.value }))} style={INPUT} />
          <input required placeholder="File URL / path" value={form.document_url} onChange={e => setForm(p => ({ ...p, document_url: e.target.value }))} style={INPUT} />
          <input placeholder="Version (e.g. 1.0)" value={form.version} onChange={e => setForm(p => ({ ...p, version: e.target.value }))} style={INPUT} />
          <input placeholder="Notes (optional)" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} style={INPUT} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="submit" disabled={saving} style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Adding…' : 'Add Document'}
          </button>
        </div>
      </form>
      <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}` }}>
        {(!tender.documents || tender.documents.length === 0) ? (
          <p style={{ padding: '1.5rem', textAlign: 'center', color: theme.textMuted, fontSize: '0.875rem' }}>No documents uploaded</p>
        ) : tender.documents.map((d, i) => (
          <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', borderBottom: i < tender.documents.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
            <span style={{ fontSize: '1.25rem' }}>📄</span>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '0.875rem', fontWeight: 500, color: theme.text, margin: 0 }}>{d.document_name}</p>
              <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0.1rem 0 0' }}>v{d.version} · {new Date(d.uploaded_at).toLocaleDateString('en-IN')}</p>
            </div>
            <a href={d.document_url} target="_blank" rel="noreferrer" style={{ fontSize: '0.8rem', color: theme.primary, textDecoration: 'none' }}>View</a>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Quotes Tab ────────────────────────────────────────────────────────────────
function QuotesTab({ tenderId }) {
  const [quotes, setQuotes] = useState([]);
  const [form, setForm] = useState({ vendor_id: '', quoted_amount: '', currency: 'INR', validity_date: '', notes: '' });
  const [vendors, setVendors] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    Promise.all([salesAPI.getQuotes(tenderId), salesAPI.getVendors()])
      .then(([q, v]) => { setQuotes(q.data); setVendors(v.data.vendors || []); })
      .catch(console.error);
  }, [tenderId]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await salesAPI.addQuote(tenderId, { ...form, quoted_amount: parseFloat(form.quoted_amount) });
      setForm({ vendor_id: '', quoted_amount: '', currency: 'INR', validity_date: '', notes: '' });
      load();
    } finally { setSaving(false); }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <form onSubmit={add} style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: theme.text, margin: 0 }}>Add Vendor Quote</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <select required value={form.vendor_id} onChange={e => setForm(p => ({ ...p, vendor_id: e.target.value }))} style={INPUT}>
            <option value="">Select Vendor</option>
            {vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
          <input required type="number" placeholder="Quoted Amount" value={form.quoted_amount} onChange={e => setForm(p => ({ ...p, quoted_amount: e.target.value }))} style={INPUT} />
          <input type="date" value={form.validity_date} onChange={e => setForm(p => ({ ...p, validity_date: e.target.value }))} style={INPUT} />
          <input placeholder="Notes" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} style={INPUT} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="submit" disabled={saving} style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Adding…' : 'Add Quote'}
          </button>
        </div>
      </form>
      <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}` }}>
        {quotes.length === 0 ? (
          <p style={{ padding: '1.5rem', textAlign: 'center', color: theme.textMuted, fontSize: '0.875rem' }}>No quotes yet</p>
        ) : quotes.map((q, i) => (
          <div key={q.id} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem 1rem', borderBottom: i < quotes.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '0.875rem', fontWeight: 500, color: theme.text, margin: 0 }}>{q.vendor_name || `Vendor #${q.vendor_id}`}</p>
              <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0.1rem 0 0' }}>
                {q.validity_date ? `Valid till ${new Date(q.validity_date).toLocaleDateString('en-IN')}` : 'No expiry'}
              </p>
            </div>
            <span style={{ fontSize: '0.75rem', padding: '0.2rem 0.6rem', borderRadius: '999px', background: q.is_selected ? '#D1FAE5' : theme.pageBg, color: q.is_selected ? '#065F46' : theme.textMuted }}>
              {q.is_selected ? 'Selected' : 'Pending'}
            </span>
            <span style={{ fontWeight: 600, color: theme.text, fontSize: '0.875rem' }}>₹{Number(q.quoted_amount).toLocaleString()}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Tasks Tab ─────────────────────────────────────────────────────────────────
function TasksTab({ tenderId }) {
  const [tasks, setTasks] = useState([]);
  const [form, setForm] = useState({ title: '', assigned_to_name: '', due_date: '', priority: 'medium' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    salesAPI.getTasks(tenderId).then(r => setTasks(r.data)).catch(console.error);
  }, [tenderId]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await salesAPI.addTask(tenderId, form);
      setForm({ title: '', assigned_to_name: '', due_date: '', priority: 'medium' });
      load();
    } finally { setSaving(false); }
  };

  const updateStatus = async (task, status) => {
    await salesAPI.updateTask(tenderId, task.id, { status });
    load();
  };

  const PRIORITY_COLOR = { low: theme.textMuted, medium: theme.info, high: theme.warning, critical: theme.danger };
  const TASK_STATUS_STYLE = {
    pending:     { background: theme.pageBg, color: theme.textMuted },
    in_progress: { background: '#DBEAFE', color: '#1D4ED8' },
    done:        { background: '#D1FAE5', color: '#065F46' },
    blocked:     { background: '#FEE2E2', color: '#991B1B' },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <form onSubmit={add} style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: theme.text, margin: 0 }}>Add Task</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <input required placeholder="Task title" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} style={{ ...INPUT, gridColumn: '1 / -1' }} />
          <input placeholder="Assigned to" value={form.assigned_to_name} onChange={e => setForm(p => ({ ...p, assigned_to_name: e.target.value }))} style={INPUT} />
          <input type="date" value={form.due_date} onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))} style={INPUT} />
          <select value={form.priority} onChange={e => setForm(p => ({ ...p, priority: e.target.value }))} style={INPUT}>
            {['low','medium','high','critical'].map(p => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="submit" disabled={saving} style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Adding…' : 'Add Task'}
          </button>
        </div>
      </form>
      <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}` }}>
        {tasks.length === 0 ? (
          <p style={{ padding: '1.5rem', textAlign: 'center', color: theme.textMuted, fontSize: '0.875rem' }}>No tasks yet</p>
        ) : tasks.map((t, i) => (
          <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 1rem', borderBottom: i < tasks.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
            <span style={{ fontSize: '1rem', color: PRIORITY_COLOR[t.priority] || theme.textMuted }}>●</span>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '0.875rem', color: theme.text, margin: 0 }}>{t.title}</p>
              {t.assigned_to_name && <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0.1rem 0 0' }}>{t.assigned_to_name}</p>}
            </div>
            {t.due_date && <span style={{ fontSize: '0.75rem', color: theme.textMuted }}>{new Date(t.due_date).toLocaleDateString('en-IN')}</span>}
            <select value={t.status} onChange={e => updateStatus(t, e.target.value)}
              style={{ ...(TASK_STATUS_STYLE[t.status] || { background: theme.pageBg, color: theme.textMuted }), fontSize: '0.75rem', padding: '0.2rem 0.5rem', borderRadius: '999px', border: 'none', fontWeight: 500, cursor: 'pointer' }}>
              {['pending','in_progress','done','blocked'].map(s => <option key={s} value={s}>{s.replace('_',' ')}</option>)}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Communications Tab ────────────────────────────────────────────────────────
function CommunicationsTab({ tenderId }) {
  const [comms, setComms] = useState([]);
  const [form, setForm] = useState({ log_type: 'email', subject: '', content: '', contact_person: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    salesAPI.getCommunications(tenderId).then(r => setComms(r.data)).catch(console.error);
  }, [tenderId]);

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await salesAPI.addCommunication(tenderId, form);
      setForm({ log_type: 'email', subject: '', content: '', contact_person: '' });
      load();
    } finally { setSaving(false); }
  };

  const TYPE_ICON = { email: '📧', call: '📞', meeting: '🤝', portal: '🌐', other: '📝', note: '📝' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <form onSubmit={add} style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: theme.text, margin: 0 }}>Log Communication</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <select value={form.log_type} onChange={e => setForm(p => ({ ...p, log_type: e.target.value }))} style={INPUT}>
            {['email','call','meeting','portal','note','other'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
          </select>
          <input placeholder="Contact person" value={form.contact_person} onChange={e => setForm(p => ({ ...p, contact_person: e.target.value }))} style={INPUT} />
          <input placeholder="Subject" value={form.subject} onChange={e => setForm(p => ({ ...p, subject: e.target.value }))} style={{ ...INPUT, gridColumn: '1 / -1' }} />
          <textarea rows={2} placeholder="Notes / Content" value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))} style={{ ...INPUT, gridColumn: '1 / -1', resize: 'none' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="submit" disabled={saving} style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Log'}
          </button>
        </div>
      </form>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {comms.length === 0 ? (
          <p style={{ textAlign: 'center', color: theme.textMuted, fontSize: '0.875rem', padding: '1.5rem 0' }}>No communications logged</p>
        ) : comms.map(c => (
          <div key={c.id} style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, padding: '1rem', display: 'flex', gap: '0.75rem' }}>
            <span style={{ fontSize: '1.25rem' }}>{TYPE_ICON[c.log_type] || '📝'}</span>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <p style={{ fontSize: '0.875rem', fontWeight: 500, color: theme.text, margin: 0 }}>{c.subject || `(${c.log_type})`}</p>
                <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: 0 }}>{c.logged_at ? new Date(c.logged_at).toLocaleDateString('en-IN') : ''}</p>
              </div>
              {c.contact_person && <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0.2rem 0 0' }}>With {c.contact_person}</p>}
              {c.content && <p style={{ fontSize: '0.875rem', color: theme.text, margin: '0.4rem 0 0' }}>{c.content}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Approvals Tab ─────────────────────────────────────────────────────────────
function ApprovalsTab({ tenderId }) {
  const [approvals, setApprovals] = useState([]);
  const [form, setForm] = useState({ approval_type: 'bid_participation', comments: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    salesAPI.getApprovals(tenderId).then(r => setApprovals(r.data)).catch(console.error);
  }, [tenderId]);

  useEffect(() => { load(); }, [load]);

  const request = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await salesAPI.addApproval(tenderId, form);
      setForm({ approval_type: 'bid_participation', comments: '' });
      load();
    } finally { setSaving(false); }
  };

  const update = async (id, status) => {
    await salesAPI.updateApproval(tenderId, id, { status });
    load();
  };

  const APPROVAL_STATUS_STYLE = {
    pending:  { background: '#FEF3C7', color: '#92400E' },
    approved: { background: '#D1FAE5', color: '#065F46' },
    rejected: { background: '#FEE2E2', color: '#991B1B' },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <form onSubmit={request} style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}`, padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: theme.text, margin: 0 }}>Request Approval</h3>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
          <select value={form.approval_type} onChange={e => setForm(p => ({ ...p, approval_type: e.target.value }))} style={INPUT}>
            {['bid_participation','pricing','final_submission'].map(t => (
              <option key={t} value={t}>{t.replace(/_/g,' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
            ))}
          </select>
          <input placeholder="Comments" value={form.comments} onChange={e => setForm(p => ({ ...p, comments: e.target.value }))} style={INPUT} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button type="submit" disabled={saving} style={{ padding: '0.5rem 1rem', fontSize: '0.875rem', background: theme.primary, color: '#fff', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Requesting…' : 'Request Approval'}
          </button>
        </div>
      </form>
      <div style={{ background: theme.cardBg, borderRadius: '0.75rem', border: `1px solid ${theme.border}` }}>
        {approvals.length === 0 ? (
          <p style={{ padding: '1.5rem', textAlign: 'center', color: theme.textMuted, fontSize: '0.875rem' }}>No approvals yet</p>
        ) : approvals.map((a, i) => (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.75rem 1rem', borderBottom: i < approvals.length - 1 ? `1px solid ${theme.border}` : 'none' }}>
            <div style={{ flex: 1 }}>
              <p style={{ fontSize: '0.875rem', fontWeight: 500, color: theme.text, margin: 0, textTransform: 'capitalize' }}>{a.approval_type?.replace(/_/g,' ')}</p>
              {a.comments && <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0.1rem 0 0' }}>{a.comments}</p>}
              <p style={{ fontSize: '0.75rem', color: theme.textMuted, margin: '0.1rem 0 0' }}>{a.requested_at ? new Date(a.requested_at).toLocaleDateString('en-IN') : ''}</p>
            </div>
            <span style={{ ...( APPROVAL_STATUS_STYLE[a.status] || { background: theme.pageBg, color: theme.textMuted }), fontSize: '0.75rem', padding: '0.2rem 0.6rem', borderRadius: '999px', fontWeight: 500, textTransform: 'capitalize' }}>
              {a.status}
            </span>
            {a.status === 'pending' && (
              <div style={{ display: 'flex', gap: '0.35rem' }}>
                <button onClick={() => update(a.id, 'approved')} style={{ fontSize: '0.75rem', background: theme.success, color: '#fff', border: 'none', padding: '0.25rem 0.5rem', borderRadius: '0.35rem', cursor: 'pointer' }}>Approve</button>
                <button onClick={() => update(a.id, 'rejected')} style={{ fontSize: '0.75rem', background: theme.danger, color: '#fff', border: 'none', padding: '0.25rem 0.5rem', borderRadius: '0.35rem', cursor: 'pointer' }}>Reject</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
